import { useState, useEffect } from 'react';
import { Link, useLocation } from 'wouter';
import { Button } from '@/components/ui/button';
import { useViewOnlyMode } from '@/hooks/use-view-only-mode';
import { useCareRecipientView } from '@/contexts/care-recipient-view-context';
import { playSound } from '@/lib/animations';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useCurrentHub } from '@/lib/hooks/use-current-hub';
import { MiniThemeSelector } from '@/components/ui/theme-selector';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import HubSetup from '@/components/ui/hub-setup';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { BugReportButton } from '@/components/bug-report-modal';
import { Lightbulb, MessageSquare } from 'lucide-react';

export default function Header() {
  const [showHubSetup, setShowHubSetup] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const isViewOnly = useViewOnlyMode();
  const { currentHubData, managedHubs } = useCurrentHub();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [showSignOutDialog, setShowSignOutDialog] = useState(false);
  const [displayHubName, setDisplayHubName] = useState('');
  const [location] = useLocation();

  const { toast } = useToast();
  const { logout } = useAuth();
  
  const hubIdFromUrl = location.match(/\/hub\/([a-f0-9-]+)/)?.[1] || null;
  
  useEffect(() => {
    if (hubIdFromUrl && managedHubs?.length > 0) {
      const matchingHub = managedHubs.find(hub => hub.id === hubIdFromUrl);
      if (matchingHub) {
        setDisplayHubName(matchingHub.name);
        return;
      }
    }
    
    if (currentHubData?.profile?.name) {
      setDisplayHubName(currentHubData.profile.name);
      return;
    }
    
    const storedName = localStorage.getItem('currentHubName');
    if (storedName) {
      setDisplayHubName(storedName);
      return;
    }
    
    if (hubIdFromUrl && !managedHubs) {
      return;
    }
    
    setDisplayHubName('Select Care Hub');
  }, [hubIdFromUrl, managedHubs, currentHubData]);

  const handleHubSetup = () => {
    // Close the sheet first
    setSheetOpen(false);

    // Open hub setup after a brief delay
    setTimeout(() => {
      setShowHubSetup(true);
    }, 100);
  };

  const handleHubDialogClose = () => {
    setShowHubSetup(false);
  };

  const handleSignOut = async () => {
    setShowSignOutDialog(false);
    await logout({
      clearStorage: true,
      logoutParams: { returnTo: window.location.origin },
    });
    toast({
      title: "Signed out successfully.",
    })
  };

  return (
    <header className="bg-white shadow-md">
      <div className="container mx-auto px-4 py-3 flex justify-between items-center">
        <Link href="/">
          <div className="flex items-center gap-2 cursor-pointer">
            <img src="/logo.png" alt="ShowMe We Can" className="h-10 w-auto" />
          </div>
        </Link>

        <div className="flex items-center gap-4">
          <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
            <SheetTrigger asChild>
              <Button
                variant="outline"
                className="flex items-center gap-2 px-4"
              >
                <Avatar className="h-6 w-6">
                  {currentHubData?.profile?.avatarUrl && currentHubData.profile.avatarUrl.trim() !== '' ? (
                    <AvatarImage 
                      src={currentHubData.profile.avatarUrl} 
                      alt={currentHubData.profile.name || 'Hub'}
                      onError={(e) => {
                        // Hide the image element if it fails to load to prevent flashing
                        const target = e.currentTarget as HTMLImageElement;
                        target.style.display = 'none';
                      }}
                    />
                  ) : null}
                  <AvatarFallback>
                    {currentHubData?.profile?.name?.charAt(0) || 'H'}
                  </AvatarFallback>
                </Avatar>
                <span className="text-sm font-semibold">
                  {displayHubName || 'Select Care Hub'}
                </span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right">
              <div className="py-4">
                <h2 className="text-lg font-semibold mb-4">Hub Options</h2>
                
                {/* Dynamic button for navigation */}
                <Button 
                  className="w-full mb-4"
                  variant="default"
                  onClick={() => {
                    setSheetOpen(false);
                    // Check if we're on a hub detail page (like settings, sharing, etc.)
                    const hubIdMatch = window.location.pathname.match(/\/hub\/([a-f0-9-]+)\/\w+/);
                    if (hubIdMatch) {
                      // If we're on a hub detail page, go back to the hub dashboard
                      window.location.href = `/hub/${hubIdMatch[1]}`;
                    } else {
                      // If we're already on a hub dashboard or somewhere else, go to hub selector
                      window.location.href = '/hubs';
                    }
                  }}
                >
                  {window.location.pathname.match(/\/hub\/([a-f0-9-]+)\/\w+/) ? 'Back to Hub Dashboard' : 'Back to Hub Selector'}
                </Button>
                
                {/* Hub Settings Section */}
                <div className="mt-4 border-t border-gray-200 pt-4">
                  <h3 className="text-sm font-semibold mb-3">Current Hub Actions</h3>
                  
                  {/* Get current hub ID from URL or localStorage */}
                  <Button
                    variant="outline"
                    className="w-full justify-start mb-2"
                    onClick={() => {
                      // First try to get hub ID from URL
                      const hubIdMatch = window.location.pathname.match(/\/hub\/([a-f0-9-]+)/);
                      let hubId = hubIdMatch ? hubIdMatch[1] : localStorage.getItem('currentHubId');
                      
                      if (!hubId) {
                        toast({
                          title: "No hub selected",
                          description: "Please select a hub first",
                          variant: "destructive"
                        });
                        return;
                      }
                      
                      setSheetOpen(false);
                      window.location.href = `/hub/${hubId}/settings/simple`;
                    }}
                  >
                    Edit Hub Settings
                  </Button>
                  
                  <Button
                    variant="outline"
                    className="w-full justify-start mb-2"
                    onClick={() => {
                      // First try to get hub ID from URL
                      const hubIdMatch = window.location.pathname.match(/\/hub\/([a-f0-9-]+)/);
                      let hubId = hubIdMatch ? hubIdMatch[1] : localStorage.getItem('currentHubId');
                      
                      if (!hubId) {
                        toast({
                          title: "No hub selected",
                          description: "Please select a hub first",
                          variant: "destructive"
                        });
                        return;
                      }
                      
                      setSheetOpen(false);
                      window.location.href = `/hub/${hubId}/sharing`;
                    }}
                  >
                    Manage Sharing
                  </Button>
                  
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => {
                      // First try to get hub ID from URL
                      const hubIdMatch = window.location.pathname.match(/\/hub\/([a-f0-9-]+)/);
                      let hubId = hubIdMatch ? hubIdMatch[1] : localStorage.getItem('currentHubId');
                      
                      if (!hubId) {
                        toast({
                          title: "No hub selected",
                          description: "Please select a hub first",
                          variant: "destructive"
                        });
                        return;
                      }
                      
                      // Force synchronization of schedules to localStorage before opening new tab
                      try {
                        // Get current schedules from the schedules page if available
                        const currentSchedules = localStorage.getItem(`schedules_${hubId}`);
                        if (currentSchedules) {
                          console.log(`[Header] Found existing schedules for hub ${hubId}, ensuring they're current`);
                          // Force a BroadcastChannel update to ensure data is fresh
                          const channel = new BroadcastChannel(`schedules_sync_${hubId}`);
                          const schedules = JSON.parse(currentSchedules);
                          channel.postMessage({
                            type: 'SCHEDULES_UPDATED',
                            schedules: schedules,
                            timestamp: Date.now()
                          });
                          channel.close();
                        }
                      } catch (error) {
                        console.error('Error syncing schedules before opening care recipient view:', error);
                      }
                      
                      // Open care recipient view in a new tab
                      setSheetOpen(false);
                      window.open(`/care-view?hubId=${hubId}`, '_blank');
                    }}
                  >
                    Open Care Recipient View
                  </Button>
                </div>

                <div className="mt-4 border-t border-gray-200 pt-4 space-y-2">
                  <Link href="/support-inbox">
                    <Button variant="outline" className="w-full justify-start gap-2" onClick={() => setSheetOpen(false)}>
                      <MessageSquare className="w-4 h-4" />
                      Support Inbox
                    </Button>
                  </Link>
                  <Link href="/beta">
                    <Button variant="outline" className="w-full justify-start gap-2" onClick={() => setSheetOpen(false)}>
                      <Lightbulb className="w-4 h-4" />
                      Beta Dashboard
                    </Button>
                  </Link>
                  <BugReportButton />
                </div>
              </div>
            </SheetContent>
          </Sheet>

          <MiniThemeSelector />

          <Link href="/profile">
            <Button
              variant="ghost"
              size="icon"
              className="bg-primary text-white rounded-full h-8 w-8 flex items-center justify-center p-0 overflow-hidden"
            >
              {(() => {
                // First check for current hub's profile picture
                const hubId = localStorage.getItem('currentHubId');
                let profilePic = null;
                
                if (hubId) {
                  // Try to load the hub-specific settings
                  try {
                    const hubData = localStorage.getItem(`simple_hub_${hubId}`);
                    if (hubData) {
                      const parsedData = JSON.parse(hubData);
                      if (parsedData && parsedData.profilePicture) {
                        profilePic = parsedData.profilePicture;
                      }
                    }
                  } catch (e) {
                    console.error('Error loading hub profile picture:', e);
                  }
                }
                
                // Fallback to global profile picture if hub-specific one isn't available
                if (!profilePic) {
                  profilePic = localStorage.getItem('userProfilePicture');
                }
                
                return profilePic ? (
                  <img 
                    src={profilePic} 
                    alt="User" 
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <span className="text-sm">U</span>
                );
              })()}
            </Button>
          </Link>
        </div>
      </div>

      <HubSetup
        open={showHubSetup}
        onClose={handleHubDialogClose}
        onHubCreated={() => {
          setShowHubSetup(false);
          playSound('success');
          
          // Manually force reload after hub creation to ensure the UI updates
          setTimeout(() => {
            window.location.reload();
          }, 500);
        }}
      />
    </header>
  );
}